import { Box, Button, CircularProgress, Snackbar, Typography, Grid, Card, CardContent, Container, TextField, ListItem, ListItemText, ListItemIcon, IconButton } from '@mui/material';
import { MdCloudUpload, MdEdit } from 'react-icons/md';
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import Nav, { USER_DATA } from '../Components/Settings/Nav';
import { CONFIG_APP_UPTIME_METRICS_BASE_URL } from '../configApp';
import { MdEmail } from 'react-icons/md';
import { useQuery } from '@apollo/client';

const faqData = [
    {
        question: "What is Trackify?",
        answer: "Trackify is an advanced tracking application designed to help you monitor activities across various platforms. It allows you to upload images and get insights based on them."
    },
    {
        question: "How do I upload an image?",
        answer: "To upload an image, simply drag and drop an image into the designated area or click to select a file from your device."
    },
    {
        question: "What happens after I upload the image?",
        answer: "Once you upload the image, it will be processed, and you will receive valuable insights based on the uploaded content."
    }
];

const Trackify = () => {
    const { error: err, data } = useQuery(USER_DATA);
    const [email, setEmail] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [image, setImage] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [imageUrl, setImageUrl] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // Fetch email from GraphQL API when the component mounts
    useEffect(() => {
        if (data) {
            setEmail(data.getUserById.email);
        }
    }, [data]);

    // Handle form input changes
    const handleEmailChange = (e) => setNewEmail(e.target.value);

    // Handle the dropzone image upload
    const handleDrop = (acceptedFiles) => {
        setImage(acceptedFiles[0]);
        setErrorMessage('');
    };

    // Handle upload form submission
    const handleUpload = async (e) => {
        e.preventDefault();

        if (!email || !image) {
            setErrorMessage('Please provide email and an image.');
            return;
        }

        // Fetch the userId from localStorage
        const userId = localStorage.getItem('userId');
        if (!userId) {
            setErrorMessage('User ID is missing. Please ensure you are logged in.');
            return;
        }

        setUploading(true);
        setErrorMessage('');

        const formData = new FormData();
        formData.append('email', email);
        formData.append('userId', userId);
        formData.append('image', image);

        try {
            const response = await axios.post(`${CONFIG_APP_UPTIME_METRICS_BASE_URL}/trackify-upload-image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setImageUrl(response.data.imageUrl);
            setUploading(false);
            setSnackbarOpen(true);  // Show success message
        } catch (err) {
            console.error('Error uploading image:', err);
            setErrorMessage('Failed to upload image. Please try again.');
            setUploading(false);
        }
    };

    // Dropzone options
    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleDrop,
        accept: 'image/*',
        maxFiles: 1,
        multiple: false,
    });

    const handleEditEmail = () => {
        setIsEditing(!isEditing);
    };

    const handleSaveEmail = () => {
        if (newEmail.trim() !== '') {
            setEmail(newEmail);
            setIsEditing(false);
            setNewEmail('');
        }
    };

    // Handle image removal
    const handleRemoveImage = () => {
        setImage(null);
        setImageUrl('');
    };

    return (
        <Box sx={{
            flexGrow: 1,
            height: '99.3vh',
            overflow: 'auto',
        }}>
            <Nav />
            {/* Title and Description */}
            <Container sx={{ maxHeight: "100vh" }}>
                <Box sx={{ paddingTop: 3, textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', marginBottom: 2, color: 'primary.main' }}>
                        Trackify by Nife
                    </Typography>
                    <Typography variant="h6" sx={{ maxWidth: 800, margin: '0 auto' }}>
                        Trackify is an advanced tracking application designed to help you monitor activities across various platforms.
                        Upload your image to start using our service and discover new insights.
                    </Typography>
                </Box>

                {/* Image and Form Section using Flex */}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    {/* Left Side - Hero Image */}
                    <Box sx={{ width: { xs: '100%', sm: '40%' }, display: 'flex', justifyContent: 'center', marginBottom: { xs: 2, sm: 0 } }}>
                        <img
                            src="https://react-nife-2024.s3.ap-south-1.amazonaws.com/oikos-features/trakify-upload.svg"
                            alt="Trackify Hero"
                            style={{
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: 8,
                            }}
                        />
                    </Box>

                    {/* Right Side - Form */}
                    <Box sx={{ width: { xs: '100%', sm: '50%' }, paddingLeft: { sm: 5 } }}>
                        {/* Email ListItem with Edit functionality */}
                        {(email || isEditing) ? (
                            <ListItem>
                                <ListItemIcon sx={{ fontSize: "xx-large" }}>
                                    <MdEmail style={{ color: "#160044" }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={email}
                                    secondary="This email will be used to upload image"
                                />
                                <IconButton edge="end" onClick={handleEditEmail}>
                                    <MdEdit />
                                </IconButton>
                            </ListItem>
                        ) : (
                            <Box sx={{ padding: 2 }}>
                                <TextField
                                    label="Update Email"
                                    type="email"
                                    value={newEmail}
                                    onChange={handleEmailChange}
                                    fullWidth
                                    margin="normal"
                                />
                                <Button
                                    onClick={handleSaveEmail}
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                >
                                    Save Email
                                </Button>
                            </Box>
                        )}

                        <form onSubmit={handleUpload} style={{ width: '100%' }}>
                            {/* Dropzone for image upload */}


                            {/* Image preview and remove option */}
                            {image ? (
                                <Box mt={2} textAlign="center">
                                    <Typography variant="h6">Ready to upload!</Typography>
                                    <img src={URL.createObjectURL(image)} alt="Selected" width="200" style={{ borderRadius: '8px', border: "10px white solid" }} />
                                    <Box sx={{ marginTop: 0 }}>
                                        <Button variant="text" sx={{ mb: 1, textTransform: "capitalize" }} onClick={handleRemoveImage}>
                                            Remove Image
                                        </Button>
                                    </Box>
                                </Box>
                            ) :
                                <Box
                                    {...getRootProps()}
                                    sx={{
                                        border: '2px dashed #1976d2',
                                        borderRadius: 2,
                                        padding: 3,
                                        height: 200,
                                        textAlign: 'center',
                                        marginBottom: 2,
                                        cursor: 'pointer',
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    }}
                                >
                                    <input {...getInputProps()} />
                                    <MdCloudUpload style={{ fontSize: "3rem", marginTop: "20px" }} />
                                    <Typography variant="body2" color="textSecondary">
                                        Drag & drop an image here, or click to select a file
                                    </Typography>
                                </Box>
                            }

                            {/* Upload Button */}
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={uploading || !image}
                            >
                                {uploading ? <CircularProgress size={24} color="inherit" /> : 'Upload Image'}
                            </Button>
                        </form>

                        {errorMessage && (
                            <Typography color="error" variant="body2" align="center" mt={2}>
                                {errorMessage}
                            </Typography>
                        )}

                        {imageUrl && (
                            <Box mt={2} textAlign="center">
                                <Typography variant="h6">Image Uploaded Successfully!</Typography>
                                <img src={imageUrl} alt="Uploaded" width="200" />
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box sx={{ backgroundColor: '#f0f0f0', padding: 5 }}>
                    <Typography variant="h5" align="center" sx={{ marginBottom: 3 }}>
                        Frequently Asked Questions
                    </Typography>
                    <Grid container spacing={4} justifyContent="center">
                        {faqData.map((faq, index) => (
                            <Grid item xs={12} sm={4} key={index}>
                                <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {faq.question}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {faq.answer}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>


                {/* Snackbar for success message */}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={() => setSnackbarOpen(false)}
                    message="Image uploaded successfully!"
                />
            </Container>
        </Box>
    );
};

export default Trackify;



